import QueryBuilder from '~/sqlite/services/queryBuilder';
import { Schemas } from '~/sqlite/schemas';
import { tableMigrationRegistry } from '~/data/db/TableMigrationRegistry';

import { DatabaseClient } from '../DatabaseClient';
import { OUTBOX_TABLE_NAME } from './constants';

type SqlStatement = { sql: string; params?: unknown[] };

const CHUNK_SIZE = 100;

export function intersectInsertColumns(
  rows: Record<string, unknown>[],
  newColumnNames: string[]
): string[] {
  if (!rows.length) {
    return newColumnNames;
  }
  const oldKeys = new Set<string>();
  rows.forEach((row) => {
    Object.keys(row).forEach((key) => oldKeys.add(key));
  });
  return newColumnNames.filter((name) => oldKeys.has(name));
}

function buildInsertStatementsForRows(
  tableName: string,
  rows: Record<string, unknown>[],
  columnNames: string[]
): SqlStatement[] {
  if (!rows.length || !columnNames.length) {
    return [];
  }

  const statements: SqlStatement[] = [];
  for (let offset = 0; offset < rows.length; offset += CHUNK_SIZE) {
    const chunk = rows.slice(offset, offset + CHUNK_SIZE);
    const params: unknown[] = [];
    const valueGroups: string[] = [];

    chunk.forEach((row) => {
      const placeholders: string[] = [];
      columnNames.forEach((col) => {
        const value = row[col];
        if (value === undefined || value === null || value === '') {
          placeholders.push('null');
          return;
        }
        if (typeof value === 'boolean') {
          params.push(value ? 1 : 0);
          placeholders.push('?');
          return;
        }
        params.push(value);
        placeholders.push('?');
      });
      valueGroups.push(`(${placeholders.join(', ')})`);
    });

    statements.push({
      sql: `INSERT INTO ${tableName} (${columnNames.join(', ')}) VALUES ${valueGroups.join(', ')}`,
      params,
    });
  }
  return statements;
}

/**
 * Drop all user tables, recreate from registry, restore SyncOfflineData in one transaction.
 */
export function runFullSchemaReset(client: DatabaseClient, targetVersion: number): Promise<void> {
  const qb = new QueryBuilder();
  const createSqls = tableMigrationRegistry.createTableStatements();
  const outboxColumns = Schemas.SyncOfflineData.map((col) => col.name);

  return new Promise((resolve, reject) => {
    client.raw.transaction(
      (tx) => {
        tx.executeSql(
          "SELECT name FROM sqlite_master WHERE type='table' AND name NOT LIKE 'sqlite_%'",
          [],
          (_t, tableResult) => {
            const tableNames: string[] = [];
            for (let i = 0; i < tableResult.rows.length; i++) {
              tableNames.push(tableResult.rows.item(i).name);
            }

            const readOutbox = (onRows: (rows: Record<string, unknown>[]) => void): void => {
              if (!tableNames.includes(OUTBOX_TABLE_NAME)) {
                onRows([]);
                return;
              }
              tx.executeSql(
                `SELECT * FROM ${OUTBOX_TABLE_NAME}`,
                [],
                (_t2, selectResult) => {
                  const rows: Record<string, unknown>[] = [];
                  for (let j = 0; j < selectResult.rows.length; j++) {
                    rows.push(selectResult.rows.item(j) as Record<string, unknown>);
                  }
                  onRows(rows);
                },
                (_t2, error) => {
                  console.error('[schemaReset] failed to read SyncOfflineData', error);
                  reject(error ?? new Error('Failed to read SyncOfflineData'));
                }
              );
            };

            readOutbox((outboxRows) => {
              let dropIdx = 0;
              const dropNext = (): void => {
                if (dropIdx >= tableNames.length) {
                  createNext();
                  return;
                }
                tx.executeSql(
                  qb.dropTable(tableNames[dropIdx++]),
                  [],
                  () => dropNext(),
                  (_t3, error) => {
                    console.error('[schemaReset] drop table failed', error);
                    reject(error ?? new Error('Schema reset drop failed'));
                  }
                );
              };

              let createIdx = 0;
              const createNext = (): void => {
                if (createIdx >= createSqls.length) {
                  restoreOutbox();
                  return;
                }
                tx.executeSql(
                  createSqls[createIdx++],
                  [],
                  () => createNext(),
                  (_t3, error) => {
                    console.error('[schemaReset] create table failed', error);
                    reject(error ?? new Error('Schema reset create failed'));
                  }
                );
              };

              const restoreOutbox = (): void => {
                const insertColumns = intersectInsertColumns(outboxRows, outboxColumns);
                const insertStmts = buildInsertStatementsForRows(
                  OUTBOX_TABLE_NAME,
                  outboxRows,
                  insertColumns
                );
                let insertIdx = 0;
                const insertNext = (): void => {
                  if (insertIdx >= insertStmts.length) {
                    tx.executeSql(
                      `PRAGMA user_version = ${targetVersion}`,
                      [],
                      () => resolve(),
                      (_t4, error) => {
                        console.error('[schemaReset] failed to set user_version', error);
                        reject(error ?? new Error('Failed to set user_version'));
                      }
                    );
                    return;
                  }
                  const { sql, params = [] } = insertStmts[insertIdx++];
                  tx.executeSql(
                    sql,
                    params,
                    () => insertNext(),
                    (_t4, error) => {
                      console.error('[schemaReset] outbox restore failed', error);
                      reject(error ?? new Error('Outbox restore failed'));
                    }
                  );
                };
                insertNext();
              };

              dropNext();
            });
          },
          (_t, error) => {
            console.error('[schemaReset] failed to list tables', error);
            reject(error ?? new Error('Schema reset failed'));
          }
        );
      },
      (err) => {
        console.error('[schemaReset] transaction failed', err);
        reject(err ?? new Error('Schema reset transaction failed'));
      }
    );
  });
}
